import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import { Orbit } from "../components/Orbit";
import { Reveal } from "../components/Reveal";
import { TransformationCards } from "../components/TransformationCards";
import { PedagogyCycle } from "../components/PedagogyCycle";

export default function Home() {
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Bilgece",
    url: "https://bilgece.life",
    slogan: "Yaşamı öğrenmenin yolu.",
    description: "Wisdom-based learning & living ekosistemi.",
  };

  return (
    <>
      <Seo jsonLd={orgLd} />

      {/* HERO */}
      <section className="relative overflow-hidden grain bg-brand-offwhite pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Link to="/programlar/bilgece-atolye" data-testid="hero-announcement" className="inline-flex items-center gap-2 bg-brand-cream border border-brand-gold-light/60 text-brand-green text-xs font-medium px-4 py-2 rounded-full mb-8">
              <MapPin size={14} className="text-brand-gold" /> Yakında Kadıköy'de Bilgece Atölye
            </Link>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-xs md:text-sm uppercase tracking-[0.25em] text-brand-gold font-semibold">
              Wisdom-Based Learning &amp; Living
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-brand-green mt-5">
              Daha çok bilmek için değil,<br /><span className="text-brand-gold italic">daha bilgece yaşamak için.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
              className="text-brand-text/75 text-lg leading-relaxed mt-7 max-w-xl">
              Bilgece; çocuklardan ebeveynlere, öğretmenlerden liderlere uzanan bir öğrenme ve yaşam ekosistemidir. Bilgiyi davranışa, deneyimi farkındalığa, farkındalığı daha iyi bir yaşama dönüştürür.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
              className="flex flex-wrap gap-4 mt-10">
              <Link to="/kesfet" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors px-8 py-4 text-sm font-semibold rounded-sm">
                Yolculuğunu Seç <ArrowRight size={18} />
              </Link>
              <Link to="/programlar/bilgelik-pedagojisi" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-brand-gold text-brand-green hover:bg-brand-gold hover:text-brand-offwhite transition-colors px-8 py-4 text-sm font-semibold rounded-sm">
                Bilgelik Pedagojisi™
              </Link>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }}>
            <Orbit />
          </motion.div>
        </div>
      </section>

      {/* TRANSFORMATION */}
      <section className="section-pad bg-brand-cream">
        <div className="container-narrow">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-4">İhtiyacından Başla</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-brand-green leading-tight">Bugün neyi dönüştürmek istiyorsun?</h2>
          </Reveal>
          <TransformationCards />
        </div>
      </section>

      {/* CORE IDEA */}
      <section className="section-pad bg-brand-green text-brand-offwhite relative overflow-hidden grain">
        <div className="container-narrow text-center max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-6">Ana Düşünce</p>
            <blockquote className="font-serif text-3xl md:text-5xl font-light leading-snug italic">
              "Bilgi tek başına dönüştürmez. Onu deneyime, deneyimi farkındalığa, farkındalığı da daha iyi bir yaşama bağlamak gerekir."
            </blockquote>
            <p className="mt-8 text-brand-gold-light tracking-widest text-sm">— BİLGELİK PEDAGOJİSİ™</p>
          </Reveal>
        </div>
      </section>

      {/* PEDAGOGY PREVIEW */}
      <section className="section-pad bg-brand-offwhite">
        <div className="container-narrow">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-4">Yaklaşımımız</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-brand-green leading-tight">Bilgeliğe giden döngü</h2>
            <p className="text-brand-text/70 mt-4 leading-relaxed">Tüm Bilgece programları aynı pedagojik temele dayanır: yönelim, uygulama, farkındalık ve paylaşım.</p>
          </Reveal>
          <PedagogyCycle />
          <div className="mt-12">
            <Link to="/programlar/bilgelik-pedagojisi" className="inline-flex items-center gap-2 text-brand-green font-semibold link-underline">
              Bilgelik Pedagojisi'ni keşfet <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-brand-cream">
        <div className="container-narrow text-center max-w-2xl">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-brand-green">Bir yolculuğa hazır mısın?</h2>
            <p className="text-brand-text/70 mt-4">Bilgece ekosistemini keşfet, sana uygun programı bul.</p>
            <Link to="/programlar" className="inline-flex items-center gap-2 mt-8 bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors px-8 py-4 text-sm font-semibold rounded-sm">
              Programları İncele <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
